import { motion } from 'framer-motion'
import { Link } from 'react-router-dom'
import { FileText, Download, ArrowRight } from 'lucide-react'
import PageHeader from '@shared/components/PageHeader'
import SEO from '@shared/components/SEO'


const TITLE_TEMPLATE_URL =
    'https://docs.google.com/document/d/1dU7IoovxU5if4_WE2Z27aIU5yLiRSukQ/edit?usp=drive_link&ouid=109041864602362182284&rtpof=true&sd=true'
const MANUSCRIPT_TEMPLATE_URL =
    'https://docs.google.com/document/d/1QqjFhpbvUEbO2Feq5UuNCdkkzMnnpdfW/edit?usp=drive_link&ouid=109041864602362182284&rtpof=true&sd=true'

const GUIDELINES: { term: string; text: React.ReactNode }[] = [
    {
        term: 'Manuscript preparation',
        text: 'Manuscripts should be prepared in accordance with the guidelines of the journal, using the official title and manuscript templates above.'
    },
    {
        term: 'Submission',
        text: 'Manuscripts should be submitted online through the journal’s website.'
    },
    {
        term: 'Authorship',
        text: 'All authors should have made significant contributions to the manuscript, and all listed authors should have approved the final version.'
    },
    {
        term: 'Originality',
        text: 'Manuscripts should be original and should not have been published previously, or be under consideration for publication elsewhere.'
    },
    {
        term: 'Plagiarism',
        text: (
            <>
                Use reputable plagiarism-detection software such as{' '}
                <em>Turnitin, Urkund or DrillBit</em>. Ensure the content is original and
                abides by the terms of the UGC{' '}
                <a
                    href="/uploads/2026/02/7771545_academic-integrity-Regulation2018.pdf"
                    target="_blank"
                    rel="noreferrer"
                    style={{ color: 'var(--color-gold-700)', borderBottom: '1px solid var(--color-gold-300)' }}
                >
                    Plagiarism Policy
                </a>
                .
            </>
        )
    },
    {
        term: 'Length',
        text: 'Manuscripts should be between 5,000 and 6,000 words, including references and appendices. The total should not exceed 6 pages in the new 2-column format.'
    },
    {
        term: 'References',
        text: 'Cite in-text using the author-date system and list alphabetically at the end. The journal follows APA style; limit references to around 15 sources per article.'
    },
    {
        term: 'Formatting',
        text: 'Manuscripts should be formatted according to the Indian Studies Review style, including headings, subheadings and reference formatting.'
    },
    {
        term: 'Abstract and keywords',
        text: 'Include an abstract of no more than 250 words and up to five keywords describing the manuscript’s main themes.'
    },
    {
        term: 'Figures and tables',
        text: 'Figures and tables should be numbered and labelled appropriately, and placed within the text at the appropriate locations.'
    },
    {
        term: 'Ethics',
        text: 'Authors should follow ethical principles in academic publishing and ensure that their research does not violate ethical standards.'
    }
]

function TemplateCard({ title, sub, href, delay }: { title: string; sub: string; href: string; delay: number }) {
    return (
        <motion.a
            href={href}
            target="_blank"
            rel="noreferrer"
            initial={{ opacity: 0, y: 12 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.45, delay }}
            className="group flex items-center gap-4 p-5 transition-colors"
            style={{ border: '1px solid var(--color-rule-strong)', background: 'var(--color-paper-50)' }}
        >
            <span
                className="inline-flex items-center justify-center flex-shrink-0"
                style={{ width: '3rem', height: '3rem', background: 'var(--color-paper-100)', color: 'var(--color-gold-700)', borderRadius: '2px' }}
            >
                <FileText size={22} />
            </span>
            <div className="min-w-0 flex-1">
                <div className="font-display" style={{ fontSize: '1.05rem', color: 'var(--color-ink-700)' }}>
                    {title}
                </div>
                <div className="text-[12.5px] mt-0.5" style={{ color: 'var(--color-ink-400)' }}>
                    {sub}
                </div>
            </div>
            <span
                className="inline-flex items-center gap-1.5 text-[11px] uppercase font-semibold whitespace-nowrap transition-colors group-hover:text-[var(--color-gold-700)]"
                style={{ letterSpacing: '0.14em', color: 'var(--color-ink-400)' }}
            >
                <Download size={14} /> Download
            </span>
        </motion.a>
    )
}

export default function Guidelines() {
    return (
        <>
            <SEO
                title="Author Guidelines"
                description="Author guidelines for the Indian Studies Review (ISR) — manuscript preparation, templates, length, referencing (APA), abstract, ethics and plagiarism requirements."
                path="/guidelines"
            />
            <PageHeader
                eyebrow="For Authors"
                title="Author Guidelines"
                lede="The author guidelines for the Indian Studies Review (ISR) are as follows. Please prepare your manuscript using the official templates below."
            />

            <section className="section">
                <div className="container-prose">
                    <div className="grid sm:grid-cols-2 gap-4">
                        <TemplateCard
                            title="Manuscript Title Template"
                            sub="Title page format · Google Docs"
                            href={TITLE_TEMPLATE_URL}
                            delay={0}
                        />
                        <TemplateCard
                            title="Manuscript Template"
                            sub="2-column body format · Google Docs"
                            href={MANUSCRIPT_TEMPLATE_URL}
                            delay={0.06}
                        />
                    </div>

                    <ol className="mt-12" style={{ borderTop: '1px solid var(--color-rule)' }}>
                        {GUIDELINES.map((g, i) => (
                            <motion.li
                                key={g.term}
                                initial={{ opacity: 0, y: 10 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true }}
                                transition={{ duration: 0.4, delay: (i % 6) * 0.04 }}
                                className="grid grid-cols-[2.25rem_1fr] gap-x-5 py-5"
                                style={{ borderBottom: '1px solid var(--color-rule)' }}
                            >
                                <div
                                    className="font-display"
                                    style={{ fontSize: '1rem', color: 'var(--color-gold-700)', letterSpacing: '0.02em' }}
                                >
                                    {String(i + 1).padStart(2, '0')}
                                </div>
                                <div>
                                    <span className="font-display" style={{ fontSize: '1.05rem', color: 'var(--color-ink-700)' }}>
                                        {g.term}.
                                    </span>{' '}
                                    <span className="prose-body" style={{ fontSize: '15.5px', color: 'var(--color-ink-600)' }}>
                                        {g.text}
                                    </span>
                                </div>
                            </motion.li>
                        ))}
                    </ol>

                    <div
                        className="mt-12 flex flex-wrap items-center justify-between gap-5 pt-9"
                        style={{ borderTop: '1px solid var(--color-rule-strong)' }}
                    >
                        <p className="lede" style={{ maxWidth: '32rem', fontSize: '1.05rem' }}>
                            Ready to submit? Review the full submission policy and checklist, then
                            register to begin.
                        </p>
                        <div className="flex flex-wrap gap-3">
                            <Link to="/submission" className="btn btn-ghost">
                                Submission policy
                            </Link>
                            <Link to="/dashboard/register" className="btn btn-gold inline-flex items-center gap-2">
                                Start a submission <ArrowRight size={16} />
                            </Link>
                        </div>
                    </div>
                </div>
            </section>
        </>
    )
}
